import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../../components/layout/Sidebar";
import api from "../../utils/api";
import toast from "react-hot-toast";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const statusColor = {
  paid:     { bg: "#f0fff4", text: "#276749", dot: "#48bb78" },
  pending:  { bg: "#fffbeb", text: "#744210", dot: "#f6ad55" },
  overdue:  { bg: "#fff5f5", text: "#742a2a", dot: "#fc8181" },
  disputed: { bg: "#faf5ff", text: "#553c9a", dot: "#b794f4" },
};

const fmt = (n) => "GHS " + Number(n || 0).toLocaleString("en-GH", { minimumFractionDigits: 2 });

const Dashboard = () => {
  const [stats,   setStats]   = useState(null);
  const [recent,  setRecent]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [year,    setYear]    = useState(new Date().getFullYear().toString());

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsRes, invoicesRes] = await Promise.all([
          api.get(`/invoices/stats?fiscalYear=${year}`),
          api.get("/invoices?limit=5&sortBy=createdAt&order=desc"),
        ]);
        setStats(statsRes.data);
        setRecent(invoicesRes.data.invoices || []);
      } catch (err) {
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [year]);

  const summary  = stats?.summary  || {};
  const byStatus = stats?.byStatus || [];
  const byMonth  = stats?.byMonth  || [];
  const maxMonth = Math.max(...byMonth.map(m => m.amount), 1);
  const totalInvoices = summary.totalInvoices || 0;

  return (
    <div style={styles.shell}>
      <Sidebar />
      <main style={styles.main}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>Dashboard</h1>
            <div style={styles.yearRow}>
              <p style={styles.pageSub}>Fiscal Year</p>
              <select style={styles.yearSelect} value={year} onChange={e => setYear(e.target.value)}>
                {["2026","2025","2024","2023"].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <Link to="/upload" style={styles.uploadBtn}>+ Upload Invoice</Link>
        </div>

        {loading ? (
          <div style={styles.loadingWrap}>Loading dashboard...</div>
        ) : (
          <>
            {/* Stat Cards */}
            <div style={styles.statsGrid}>
              {[
                { label: "Total Invoices",  value: totalInvoices,                sub: "this fiscal year",      accent: "#63b3ed", isNum: true  },
                { label: "Total Amount",    value: fmt(summary.totalAmount),      sub: "across all invoices",   accent: "#f6ad55", isNum: false },
                { label: "VAT Collected",   value: fmt(summary.totalVat),         sub: "15% standard rate",     accent: "#68d391", isNum: false },
                { label: "Pending Amount",  value: fmt(summary.pendingAmount),    sub: "awaiting payment",      accent: "#fc8181", isNum: false },
              ].map((card) => (
                <div key={card.label} style={styles.statCard}>
                  <div style={{ ...styles.statAccent, background: card.accent }} />
                  <div style={styles.statLabel}>{card.label}</div>
                  <div style={styles.statValue}>{card.isNum ? card.value : card.value}</div>
                  <div style={styles.statSub}>{card.sub}</div>
                </div>
              ))}
            </div>

            {/* Charts Row */}
            <div style={styles.chartsRow}>
              {/* Monthly Bar Chart */}
              <div style={styles.chartCard}>
                <div style={styles.chartTitle}>Monthly Volume</div>
                {byMonth.length === 0 ? (
                  <div style={styles.emptyChart}>No data for this period</div>
                ) : (
                  <div style={styles.barChart}>
                    {byMonth.map((m) => (
                      <div key={m._id} style={styles.barCol}>
                        <div style={styles.barWrap}>
                          <div style={{ ...styles.bar, height: `${(m.amount / maxMonth) * 100}%` }} />
                        </div>
                        <div style={styles.barLabel}>{MONTHS[m._id - 1]}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Status Breakdown */}
              <div style={styles.chartCard}>
                <div style={styles.chartTitle}>By Status</div>
                {byStatus.length === 0 ? (
                  <div style={styles.emptyChart}>No data for this period</div>
                ) : (
                  <div style={styles.statusList}>
                    {byStatus.map((s) => {
                      const c = statusColor[s._id] || statusColor.pending;
                      const pct = totalInvoices > 0 ? Math.round((s.count / totalInvoices) * 100) : 0;
                      return (
                        <div key={s._id} style={styles.statusRow}>
                          <div style={styles.statusLeft}>
                            <div style={{ ...styles.statusDot, background: c.dot }} />
                            <span style={styles.statusName}>{s._id}</span>
                          </div>
                          <div style={styles.statusBar}>
                            <div style={{ ...styles.statusFill, width: `${pct}%`, background: c.dot }} />
                          </div>
                          <span style={styles.statusPct}>{pct}%</span>
                          <span style={styles.statusCount}>{s.count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Recent Invoices */}
            <div style={styles.tableCard}>
              <div style={styles.tableHeader}>
                <div style={styles.chartTitle}>Recent Invoices</div>
                <Link to="/invoices" style={styles.viewAll}>View all →</Link>
              </div>
              {recent.length === 0 ? (
                <div style={styles.emptyTable}>
                  No invoices yet. <Link to="/upload" style={styles.uploadLink}>Upload your first one →</Link>
                </div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {["Invoice #","Vendor","Date","Amount","Status"].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((inv, i) => {
                      const c = statusColor[inv.status] || statusColor.pending;
                      return (
                        <tr key={inv._id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                          <td style={styles.td}><span style={styles.invNum}>{inv.invoiceNumber || "—"}</span></td>
                          <td style={styles.td}>{inv.vendor?.name || "—"}</td>
                          <td style={styles.td}>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString("en-GH") : "—"}</td>
                          <td style={styles.td}><strong>{fmt(inv.amount?.totalAmount)}</strong></td>
                          <td style={styles.td}>
                            <span style={{ ...styles.badge, background: c.bg, color: c.text }}>
                              <span style={{ ...styles.badgeDot, background: c.dot }} />
                              {inv.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const styles = {
  shell: { display:"flex", minHeight:"100vh", background:"#f7f8fc", fontFamily:"sans-serif" },
  main: { flex:1, padding:"36px 40px", overflowY:"auto" },
  header: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"28px" },
  pageTitle: { fontSize:"24px", fontWeight:"bold", color:"#0a0f1e", marginBottom:"4px" },
  yearRow: { display:"flex", alignItems:"center", gap:"8px" },
  pageSub: { fontSize:"13px", color:"#718096", margin:0 },
  yearSelect: { fontSize:"13px", color:"#0a0f1e", border:"1px solid #e2e8f0", borderRadius:"6px", padding:"3px 8px", outline:"none", background:"#fff" },
  uploadBtn: { background:"#0a0f1e", color:"#fff", padding:"10px 20px", borderRadius:"8px", fontSize:"13px", fontWeight:"bold", textDecoration:"none", letterSpacing:"0.3px" },
  loadingWrap: { display:"flex", justifyContent:"center", alignItems:"center", height:"300px", color:"#a0aec0", fontSize:"14px" },
  statsGrid: { display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:"16px", marginBottom:"24px" },
  statCard: { background:"#fff", borderRadius:"12px", padding:"20px", border:"1px solid #e2e8f0", position:"relative", overflow:"hidden" },
  statAccent: { position:"absolute", top:0, left:0, right:0, height:"3px" },
  statLabel: { fontSize:"11px", color:"#718096", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:"10px", marginTop:"8px" },
  statValue: { fontSize:"22px", fontWeight:"bold", color:"#0a0f1e", marginBottom:"4px" },
  statSub: { fontSize:"11px", color:"#a0aec0" },
  chartsRow: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"24px" },
  chartCard: { background:"#fff", borderRadius:"12px", padding:"24px", border:"1px solid #e2e8f0" },
  chartTitle: { fontSize:"14px", fontWeight:"bold", color:"#0a0f1e", marginBottom:"20px" },
  emptyChart: { color:"#a0aec0", fontSize:"13px", textAlign:"center", padding:"32px 0" },
  barChart: { display:"flex", alignItems:"flex-end", gap:"8px", height:"120px" },
  barCol: { display:"flex", flexDirection:"column", alignItems:"center", flex:1, height:"100%" },
  barWrap: { flex:1, display:"flex", alignItems:"flex-end", width:"100%" },
  bar: { width:"100%", background:"#f6ad55", borderRadius:"4px 4px 0 0", minHeight:"4px" },
  barLabel: { fontSize:"10px", color:"#a0aec0", marginTop:"6px" },
  statusList: { display:"flex", flexDirection:"column", gap:"16px" },
  statusRow: { display:"flex", alignItems:"center", gap:"10px" },
  statusLeft: { display:"flex", alignItems:"center", gap:"8px", width:"80px" },
  statusDot: { width:"8px", height:"8px", borderRadius:"50%", flexShrink:0 },
  statusName: { fontSize:"12px", color:"#4a5568", textTransform:"capitalize" },
  statusBar: { flex:1, height:"6px", background:"#f0f0f0", borderRadius:"3px", overflow:"hidden" },
  statusFill: { height:"100%", borderRadius:"3px" },
  statusPct: { fontSize:"12px", color:"#718096", width:"32px", textAlign:"right" },
  statusCount: { fontSize:"12px", fontWeight:"bold", color:"#0a0f1e", width:"24px", textAlign:"right" },
  tableCard: { background:"#fff", borderRadius:"12px", border:"1px solid #e2e8f0", overflow:"hidden" },
  tableHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 24px", borderBottom:"1px solid #f0f0f0" },
  viewAll: { fontSize:"13px", color:"#f6ad55", textDecoration:"none", fontWeight:"bold" },
  emptyTable: { padding:"48px", textAlign:"center", color:"#a0aec0", fontSize:"14px" },
  uploadLink: { color:"#f6ad55", fontWeight:"bold", textDecoration:"none" },
  table: { width:"100%", borderCollapse:"collapse" },
  th: { textAlign:"left", padding:"12px 24px", fontSize:"11px", color:"#a0aec0", textTransform:"uppercase", letterSpacing:"0.5px", fontWeight:"600" },
  td: { padding:"14px 24px", fontSize:"13px", color:"#2d3748" },
  trEven: { background:"#fff" },
  trOdd: { background:"#fafafa" },
  invNum: { fontFamily:"monospace", fontWeight:"bold", color:"#0a0f1e", fontSize:"13px" },
  badge: { display:"inline-flex", alignItems:"center", gap:"5px", padding:"4px 10px", borderRadius:"20px", fontSize:"11px", fontWeight:"600", textTransform:"capitalize" },
  badgeDot: { width:"6px", height:"6px", borderRadius:"50%" },
};

export default Dashboard;